import { SharedSpinner } from '@components';
import { useMotHistoryByVin, useReminders, useVehicleReminders } from '@hooks';
import { useStore } from '@stores';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Presentation from './OrderVehicleReminder.presentation';

interface IProps {
  customerId: string;
  currentMileage: number | undefined;
  registrationNumber: string;
  vin: string;
  compact?: boolean;
}

export const OrderVehicleReminder: React.FC<IProps> = observer(
  ({ vin, customerId, currentMileage, registrationNumber, compact = false }) => {
    const { reminderStore } = useStore();
    const [formUpdate] = Form.useForm();
    const [loadingIndices, setLoadingIndices] = useState<Set<number>>(new Set());

    const remindersHook = useReminders();
    const vehicleRemindersHook = useVehicleReminders(vin);

    const withCardLoading = useCallback(async (index: number, fn: () => Promise<void>) => {
      setLoadingIndices((prev) => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
      try {
        await fn();
      } finally {
        setLoadingIndices((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
    }, []);

    const isCardLoading = useCallback((index: number) => loadingIndices.has(index), [loadingIndices]);

    const { averageMonthlyMileage } = useMotHistoryByVin(vin);

    const contentSignature = useMemo(
      () =>
        vehicleRemindersHook.vehicleReminders
          .map((r) => `v:${r.id}:${r.lastModificationTime}:${r.months}:${r.mileage}:${r.dueDate ?? ''}`)
          .join('|') +
        '##' +
        remindersHook.reminders.map((r) => `r:${r.id}`).join('|'),
      [vehicleRemindersHook.vehicleReminders, remindersHook.reminders]
    );

    useEffect(() => {
      const vehicleReminders = vehicleRemindersHook.vehicleReminders.map((reminder) => ({
        ...reminder,
        dueDate: reminder.dueDate ? dayjs(reminder.dueDate) : null,
        lastModificationTime: dayjs(reminder.lastModificationTime).format('DD.MM.YYYY'),
      }));

      const uniqueReminders = remindersHook.reminders
        .filter(
          (reminder) =>
            !vehicleReminders.some((vehicleReminder) => vehicleReminder.reminderId === reminder.id)
        )
        .map((reminder) => ({
          ...reminder,
          id: 0,
          reminderId: reminder.id,
          dueDate: null,
        }));

      const sortedReminders = [...vehicleReminders, ...uniqueReminders].sort((a, b) =>
        (b.title ?? '').localeCompare(a.title ?? '')
      );

      const mapSorted = sortedReminders.map((reminder) => ({
        ...reminder,
        dueDate: reminder.dueDate?.format('DD.MM.YYYY'),
      }));

      formUpdate.setFieldsValue({ reminders: mapSorted });
    }, [contentSignature]); // eslint-disable-line react-hooks/exhaustive-deps

    const onUpdate = useCallback(
      async (index: number) => {
        try {
          await formUpdate.validateFields([['reminders', index, 'months']]);
        } catch {
          return;
        }
        await withCardLoading(index, async () => {
          const reminders = formUpdate.getFieldValue('reminders') || [];
          const rowData = reminders[index];

          const parsedDueDate = rowData.dueDate
            ? dayjs(rowData.dueDate, 'DD.MM.YYYY')
            : null;

          await reminderStore.createOrUpdateVehicleReminder({
            ...rowData,
            vin,
            customerId,
            registrationNumber,
            months: Number(rowData.months) || 0,
            mileage: Number(rowData.mileage) || 0,
            dueMileage: Number(rowData.dueMileage) || 0,
            dueDate:
              parsedDueDate && parsedDueDate.isValid()
                ? parsedDueDate.format()
                : dayjs().format(),
          });
        });
      },
      [registrationNumber, vin, customerId, withCardLoading]
    );

    const onClear = useCallback(
      async (index: number) => {
        await withCardLoading(index, async () => {
          const reminders = formUpdate.getFieldValue('reminders') || [];
          const rowData = reminders[index];

          await reminderStore.clearVehicleReminder({
            id: rowData.id,
            vin,
            registrationNumber,
          });
        });
      },
      [registrationNumber, vin, withCardLoading]
    );

    const handleValuesChange = (changedValues: any) => {
      const reminders = [...(formUpdate.getFieldsValue(true).reminders || [])];

      changedValues.reminders?.forEach(
        (changedReminder: { months?: number; mileage?: number }, index: number) => {
          if (!changedReminder) return;
          const existingReminder = { ...(reminders[index] || {}) };

          if (changedReminder.months || changedReminder.mileage) {
            let mileageBasedMonths: number | undefined = undefined;

            if (changedReminder.mileage && averageMonthlyMileage > 0) {
              mileageBasedMonths = Math.round(
                Number(changedReminder.mileage) / averageMonthlyMileage
              );
            } else if (existingReminder.mileage && averageMonthlyMileage > 0) {
              mileageBasedMonths = Math.round(
                Number(existingReminder.mileage) / averageMonthlyMileage
              );
            }

            if (changedReminder.mileage) {
              existingReminder.dueMileage =
                (Number(currentMileage) || 0) + (Number(changedReminder.mileage) || 0);
            }

            let monthsToAdd: number;
            if (mileageBasedMonths !== undefined) {
              monthsToAdd = Math.min(
                changedReminder.months ?? existingReminder.months ?? 0,
                mileageBasedMonths
              );
            } else {
              monthsToAdd = changedReminder.months ?? existingReminder.months ?? 0;
            }

            existingReminder.dueDate = dayjs().add(monthsToAdd, 'months').format('DD.MM.YYYY');
          }

          reminders[index] = existingReminder;
        }
      );

      formUpdate.setFieldsValue({ reminders });
    };

    return (
      <SharedSpinner loading={vehicleRemindersHook.loading}>
        <Presentation
          formUpdate={formUpdate}
          onUpdate={onUpdate}
          onClear={onClear}
          handleValuesChange={handleValuesChange}
          isCardLoading={isCardLoading}
          compact={compact}
        />
      </SharedSpinner>
    );
  }
);
